import React, { useEffect, useRef, useState } from 'react';
import Tooltip from 'rc-tooltip';
import Slider from 'rc-slider';
import Label from '../../../../components/controls/Form/components/Label';
import FormGroup from '../../../../components/controls/Form/components/FormGroup';
import TextareaField from '../../../../components/controls/Form/components/TextAreaField';
import { COMMENT_TEXT_LIMIT } from '../../../../utils/Constants';
import FormControlError from '../../../../components/controls/Form/components/FormControlError';
import useRecommendation, { useQuestionBlock, useQuestionById } from '../../../../utils/useRecommendation';
import { getArrow } from "perfect-arrows"

import 'rc-slider/assets/index.css';
import 'rc-tooltip/assets/bootstrap.css';
import './Rubrics.less';
import { Alert } from '../../../../components/visual/Alert/Alert';

const TRACKER_STYLES = {
  0: {
    railWidth: '100%',
  },
  2: {
    railWidth: '81%',
    0: { backgroundColor: 'var(--main-likert-level-2)', left: (21 + (80 - 21) / 1 * 0) + '%', },
    1: { backgroundColor: 'var(--main-likert-level-5)', left: (21 + (80 - 21) / 1 * 1) + '%', },
  },
  3: {
    railWidth: '89%',
    0: { backgroundColor: 'var(--main-likert-level-0)', left: (13 + (88 - 13) / 2 * 0) + '%', },
    1: { backgroundColor: 'var(--main-likert-level-2)', left: (13 + (88 - 13) / 2 * 1) + '%', },
    2: { backgroundColor: 'var(--main-likert-level-5)', left: (13 + (88 - 13) / 2 * 2) + '%', },
  },
  4: {
    railWidth: '92%',
    0: { backgroundColor: 'var(--main-likert-level-0)', left: (13 + (91 - 13) / 3 * 0) + '%', },
    1: { backgroundColor: 'var(--main-likert-level-1)', left: (13 + (91 - 13) / 3 * 1) + '%', },
    2: { backgroundColor: 'var(--main-likert-level-3)', left: (13 + (91 - 13) / 3 * 2) + '%', },
    3: { backgroundColor: 'var(--main-likert-level-5)', left: (13 + (91 - 13) / 3 * 3) + '%', },
  },
  5: {
    railWidth: '96%',
    0: { backgroundColor: 'var(--main-likert-level-0)', left: (10 + (95 - 10) / 4 * 0) + '%', },
    1: { backgroundColor: 'var(--main-likert-level-1)', left: (10 + (95 - 10) / 4 * 1) + '%', },
    2: { backgroundColor: 'var(--main-likert-level-2)', left: (10 + (95 - 10) / 4 * 2) + '%', },
    3: { backgroundColor: 'var(--main-likert-level-4)', left: (10 + (95 - 10) / 4 * 3) + '%', },
    4: { backgroundColor: 'var(--main-likert-level-5)', left: (10 + (95 - 10) / 4 * 4) + '%', },
  },
  6: {
    railWidth: '99%',
    0: { backgroundColor: 'var(--main-likert-level-0)', left: (10 + (98 - 10) / 5 * 0) + '%', },
    1: { backgroundColor: 'var(--main-likert-level-1)', left: (10 + (98 - 10) / 5 * 1) + '%', },
    2: { backgroundColor: 'var(--main-likert-level-2)', left: (10 + (98 - 10) / 5 * 2) + '%', },
    3: { backgroundColor: 'var(--main-likert-level-3)', left: (10 + (98 - 10) / 5 * 3) + '%', },
    4: { backgroundColor: 'var(--main-likert-level-4)', left: (10 + (98 - 10) / 5 * 4) + '%', },
    5: { backgroundColor: 'var(--main-likert-level-5)', left: (10 + (98 - 10) / 5 * 5) + '%', },
  },
};

const changedSliders = (/** @type {Set<Number|undefined>} */(new Set()));

const LikertHeader = () => {
  const recommendation = useRecommendation();
  const [svgWidth, setSvgWidth] = useState(0);
  const [svgHeight, setSvgHeight] = useState(0);

  useEffect(() => {
    const headerRef = document.getElementById('likert-header');
    const headerFloatingRef = document.getElementById('likert-floating-header');
    const observer = new IntersectionObserver(
      ([e]) => headerFloatingRef?.classList.toggle("sticky", e.intersectionRatio < 1),
      { threshold: [1] }
    );

    headerRef ? observer.observe(headerRef) : void 0;

    // observe the svg width and height
    const svgRef = document.querySelector('#likert-header');
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setSvgWidth(width);
        setSvgHeight(height);
      }
    });
    svgRef ? resizeObserver.observe(svgRef) : void 0;

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
    }
  }, []);

  return <div id="likert-header">
    <div id="likert-floating-header" className="floating-panel">

      <div className={`lsn-rubrics-rating-items`}>
        {recommendation.item?.evaluationFormRubricsRatings.map((rating, index, items) => (
          <div
            className={`lsn-rubrics-level-item lsn-rubrics-level-${index} ${index === 0 ? 'first' : index === items.length - 1 ? 'last' : 'middle'}`}
            key={`rating.${rating.id}.wrapper`}
            data-size={items.length}
            style={{
              // @ts-ignore
              '--js-width-perc': (100 / (items.length)) + '%',
              '--js-ratings-count': items.length,
              '--js-left-perc': TRACKER_STYLES[items.length][index]?.left,//(100 / items.length) * (index) + '%',
              '--js-component-color': TRACKER_STYLES[items.length][index]?.backgroundColor,
              '--js-index': index,
            }}
          >
            <i className={`_for-svg-positionin _pos_${index}`}></i>
            {rating.ratingName}
            <Tooltip
              key={`rating.${rating.id}.tooltip`}
              showArrow
              prefixCls="rc-slider-tooltip"
              overlay={rating.aliasText || rating.ratingName || 'Nothing'}
              placement="bottom"
              overlayStyle={{ color: 'var(--main-black) !important' }}
            >
              <i className="far fa-info-circle lsn-rubrics-levels-tooltip" />
            </Tooltip>
            <i className='shadow-dot'></i>
          </div>
        ))}
      </div>

      {/* Bottom chevrons. The panel is the same widths as the slider track */}
      <div className={`lsn-rubrics-rating-marks-array`}>
        {/* Chewrons, aligned with the slider dots */}
        {recommendation.item?.evaluationFormRubricsRatings.map((_, index, items) => (
          <i className={`far fa-caret-down lsn-rubrics-rating-mark pos-${index} pos-${index === 0 ? 'first' : index === items.length - 1 ? 'last' : 'middle'}`} style={{
            position: 'absolute',
            bottom: '0',
            // @ts-ignore
            '--js-left-perc': TRACKER_STYLES[items.length][index]?.left,
            '--js-component-color': TRACKER_STYLES[items.length][index]?.backgroundColor,
          }} />
        ))}
      </div>
      <svg
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        id={`lsn-rubrics-rating-arrows-svg`}
        style={{ display: '', position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, zIndex: -1 }}
      >
        {
          recommendation.item?.evaluationFormRubricsRatings.map((_, index, items) => {
            // get the tooltip position
            const tipEl = (/** @type {undefined|HTMLElement} */(document.querySelector(`.lsn-rubrics-level-${index}`)));
            const markEl = (/** @type {undefined|HTMLElement} */(document.querySelector(`#likert-header .lsn-rubrics-rating-mark.pos-${index}`)));
            if (!tipEl || !markEl) return null;

            const p1 = { x: 40, y: tipEl.offsetTop }
            const p2 = { x: 10 + 5 * index, y: tipEl.offsetTop + 10 }

            const arrow = getArrow(p1.x, p1.y, p2.x, p2.y, {
              stretch: .2,
            })
            const [sx, sy, cx, cy, ex, ey] = arrow;

            const l2 = getArrow(ex, ey, p2.x, svgHeight - 10 - (index * 6));
            const [sx2, sy2, , , ex2, ey2] = l2;
            const l3 = getArrow(ex2, ey2, markEl.offsetLeft + 10, svgHeight - 10 - (index * 2));
            const [sx3, sy3, , , ex3, ey3] = l3;
            const l4 = getArrow(ex3, ey3, markEl.offsetLeft + 22, svgHeight);
            const [sx4, sy4, , , ex4, ey4] = l4;

            const designProps = {
              stroke: `color-mix(in srgb, ${TRACKER_STYLES[items.length][index]?.backgroundColor} 100%, black 10%)`,
              fill: `color-mix(in srgb, ${TRACKER_STYLES[items.length][index]?.backgroundColor} 100%, black 10%)`,
              strokeWidth: 2,
            };

            return <>
              <circle cx={sx} cy={sy} r={4} {...designProps} />
              {/* left flipper line */}
              <path d={`M${sx},${sy} Q${cx},${cy} ${ex},${ey}`} {...designProps} fill="none" />
              {/* line goes to the bottom */}
              <path d={`M${sx2},${sy2} ${ex2},${ey2}`} {...designProps} fill="none" />
              {/* half horizontal line */}
              <path d={`M${sx3},${sy3} ${ex3},${ey3}`} {...designProps} fill="none" />
              {/* connect end of the horizontal line with the marker */}
              <path d={`M${sx4},${sy4} ${ex4},${ey4}`} {...designProps} fill="none" />
            </>
          })
        }

      </svg>
    </div>
  </div>
}

/**
 *
 * @param {{ questionId: number; qIndex: number }} props
    */
const RatingQuestion = (props) => {
  const questionWrapper = useQuestionById(props.questionId);
  const [isDirty, setDirty] = useState(false);
  const commentRef = useRef(/** @type {null | HTMLTextAreaElement} */(null));
  const runtimeCommentTextRef = useRef(/** @type {null | string} */(questionWrapper.getFirstUserComment()));

  const isValid = () => {
    return questionWrapper.ratings.isValid();
  };

  /**
   *
   * @param {string} value
    */
  const onCommentInput = (value) => {
    runtimeCommentTextRef.current = value;
    questionWrapper.setUserSingleComment(value);
    setDirty(true);
  }

  /**
   *
   * @param {number} value
    */
  const handleSliders = (value) => {
    changedSliders.add(questionWrapper.question?.id);
    questionWrapper.ratings.setRatingValue(value);
    setDirty(true);
  };

  /** @type {import("rc-slider/lib/Handles/Handle").HandleProps['render']} */
  const handleTooltip = (el, handleProps) => {
    const { dragging, index } = handleProps;
    const ratingItem = questionWrapper.ratings.getRatingItem();
    return (
      <Tooltip
        showArrow
        prefixCls="rc-slider-tooltip"
        overlay={ratingItem?.aliasText || ratingItem?.ratingName || 'Nothing'}
        visible={dragging}
        placement="top"
        key={`tooltip.slider.${index}`}
      >
        {el}
      </Tooltip >
    );
  }

  // restore the height of the comment area input
  useEffect(() => {
    if (commentRef.current) {
      commentRef.current.style.height = '';
      commentRef.current.style.height = commentRef.current.scrollHeight + 'px';
    }
  }, [commentRef.current]);

  return questionWrapper.question?.id && questionWrapper.ratings.count ? (
    <div className={`columns lsn-rubrics-wrapper lsn-rubrics-wrapper-of-${props.qIndex}`} data-block-name={questionWrapper.questionRelatedBlock?.blockName}>
      <div className="column col-12 lsn-rubrics-label">
        <Label required={questionWrapper.question.required} text={questionWrapper.question.questionText} />
      </div>
      <div className="column col-12 p-relative">
        <input
          type={'text'}
          tabIndex={-1}
          aria-hidden={true}
          name={`likert_question_${questionWrapper.question.id}`}
          required={questionWrapper.question.required}
          onInvalid={() => setDirty(true)}
          defaultValue={isValid() ? questionWrapper.ratings.getRatingNumber() : ''}
          style={{ transform: 'translate(50%)' }}
        />
        <Slider
          dots
          step={1}
          min={-1}
          max={questionWrapper.ratings.count - 1}
          onBlur={() => setDirty(true)}
          onFocus={() => setDirty(true)}
          className={`lnx-rc-slider-wrapper lnx-rc-slider-sizeof-${questionWrapper.ratings.count} lnx-rc-slider-at-${questionWrapper.ratings.getRatingNumber() + 1} ${(isDirty && !isValid()) ? 'invalid' : 'valid'}`}
          value={questionWrapper.ratings.getRatingNumber()}
          styles={{
            rail: {
              width: TRACKER_STYLES[questionWrapper.ratings.count]?.railWidth,
            },
            handle: {
              left: TRACKER_STYLES[questionWrapper.ratings.count][questionWrapper.ratings.getRatingNumber()]?.left
            },
            track: {
              backgroundColor: TRACKER_STYLES[questionWrapper.ratings.count][questionWrapper.ratings.getRatingNumber()]?.backgroundColor,
              width: TRACKER_STYLES[questionWrapper.ratings.count][questionWrapper.ratings.getRatingNumber()]?.left
            }
          }}
          onChange={value => handleSliders(Array.isArray(value) ? value[0] : value)}
          handleRender={(el, sliderProps) => handleTooltip(el, sliderProps)}
          dotStyle={(dotValue) => TRACKER_STYLES[questionWrapper.ratings.count][dotValue]}
          ariaLabelForHandle={[questionWrapper.questionRelatedBlock?.blockName, questionWrapper.questionRelatedBlock?.instructions, `The slider of ${questionWrapper.question?.questionText
            }. Use left and right arrow keys to move the slider.`].filter(Boolean).join('. ')}
          ariaValueTextFormatterForHandle={value => `${questionWrapper.ratings.getRatingItemAt(value)?.ratingName || 'Nothing'} selected.`}
        />
        {isDirty && !isValid() ? (
          <FormControlError show text='Entry is required' />
        ) : (
          void 0
        )}
        {
          questionWrapper.getQuestionFirstDetails()?.commentsEnabled ?
            <div className={`comment-wrapper ${(isDirty && !isValid()) ? 'invalid' : ''} ${questionWrapper.getQuestionFirstDetails()?.commentsRequired ? 'comment-required' : ''}`}>
              <div className={`lsn-rubrics-rating-marks-array`}>
                <i className={`far fa-caret-up lsn-rubrics-rating-mark single up pos-${questionWrapper.ratings.getRatingNumber() + 1} pos-${questionWrapper.ratings.getRatingNumber() === 0 ? 'first' : questionWrapper.ratings.getRatingNumber() + 1 === questionWrapper.ratings.count ? 'last' : 'middle'}`} style={{
                  position: 'absolute',
                  top: '0',
                  left: TRACKER_STYLES[questionWrapper.ratings.count][questionWrapper.ratings.getRatingNumber()]?.left,
                }} />
              </div>
              <FormGroup
                label={<i className={`far fa-comment-alt-lines`}></i>}
                required={questionWrapper.ratings.getRatingNumber() >= 0 && questionWrapper.getQuestionFirstDetails()?.commentsRequired}
                horizontal
                classNameControlWrapper='col-12'
                controlRender={params => <TextareaField
                  autoExpand
                  title='Comment'
                  autoSelectWhenEnabled={changedSliders.has(questionWrapper.question?.id)}
                  disabled={questionWrapper.ratings.getRatingNumber() < 0}
                  limit={COMMENT_TEXT_LIMIT}
                  placeholder={questionWrapper.getQuestionFirstDetails()?.commentsLabel}
                  value={questionWrapper.getFirstUserComment() ?? runtimeCommentTextRef.current}
                  onChange={(n, value) => onCommentInput(value)}
                  {...params}
                />}
              />
            </div> : void 0}
      </div>
    </div>
  ) : questionWrapper.ratings.count === 0 ? (
    <Alert>No ratings available.</Alert>
  ) : null;
}

/**
 * @typedef {import('../../../../store/reducers/dashboardReducer').TEvaluationBlock} TEvaluationBlock
    * @param {{
      *   questionBlockId: number,
      * }} props
    * @returns
    */

const Rubrics = (props) => {
  const { item: block, ratings: { count: ratingsCount } } = useQuestionBlock(props.questionBlockId);

  // clear the cache on unmount for all
  useEffect(() => {
    return () => {
      changedSliders.clear();
    }
  }, []);

  return block ? (
    <div className="lsn-question-block lsn-question-block-rubrics lsn-question-block-ratings">
      {ratingsCount ? <LikertHeader /> : null}
      <div className="lsn-rubrics-scales">
        {block.evaluationQuestions.map((question, qIndex, items) => (<>
          <RatingQuestion questionId={question.id} qIndex={qIndex} key={`_rating_question_${question.id}`} />
          {items.length === qIndex + 1 ? void 0 : <div className='divider'></div>}
        </>
        ))}
      </div>
    </div>
  ) : null;
}

export default Rubrics;
